// Every planner action, once, as a named command.
//
// ── WHY A COMMAND LAYER ─────────────────────────────────────────────────────────
// A board that feels like a tool has four ways to do the same thing: a keyboard shortcut, a
// right-click, a button, and a long-press on a phone. Written as four handlers they drift — the menu
// grows an action the keyboard never learns, the phone gets a variant that shortens a job where the
// desktop lengthens it. So there is one catalogue, and all four surfaces render and dispatch FROM it.
// Add a command here and it appears in the context menu, answers to its key, and shows up in the
// command palette, because none of those three know anything except how to read this list.
//
// ── EVERY COMMAND CARRIES ITS OWN INVERSE ───────────────────────────────────────
// `run` returns an `undo` thunk built from what the server actually answered — the revision it handed
// back, the assignment id it created. Undo is therefore not a second implementation of every action in
// reverse, and it cannot drift from the thing it undoes, because it is written in the same twenty
// lines. A command that genuinely cannot be undone returns no thunk and says so, rather than offering
// an undo that quietly does nothing.
//
// ── WHY BULK ACTIONS ARE SEQUENTIAL ─────────────────────────────────────────────
// `for … await` and not `Promise.all`. These are guarded writes against one row each, and the
// deployment rules read OTHER assignments on the same workpackage while deciding: two parallel writes
// to one job can both see a state neither of them ends in. Slower and correct beats fast and racy on
// an operation a planner performs a handful of times a minute.
import * as A from '../api/planning.ts';
import { correlationId } from './mutate.ts';
import { addDays, daysBetween, type Ymd } from './dates.ts';
import { workpackages, pairs } from './selection.ts';
import type { PanelSubject } from './panel.ts';

export interface CmdCtx {
  /** The rows the command acts on, already resolved from the selection. */
  selection: A.ScheduleItem[];
  /** Every row on screen — for finding a job's other people or days. */
  all: A.ScheduleItem[];
  /** The day the calendar is looking at, for commands that need a destination. */
  anchor: Ymd;
  /** The clipboard, for copy/paste. Owned by the caller so it survives a re-render. */
  clipboard: A.ScheduleItem[];
  setClipboard: (items: A.ScheduleItem[]) => void;
  /** Open the side panel prefilled — for the two operations that genuinely need a decision. */
  openPanel: (s: PanelSubject) => void;
  crew: A.CrewMember[];
  people: A.Person[];
}

export interface CmdResult {
  message?: string;
  /** Undo this command. Absent when it cannot be undone. */
  undo?: () => Promise<void>;
  /** What the message should be if the undo is taken. */
  undoLabel?: string;
}

export interface Command {
  id: string;
  /** Shown in the menu and the palette. A function so it can count what it will act on. */
  label: (c: CmdCtx) => string;
  /** `mod` is Cmd on a Mac and Ctrl elsewhere. Matched by `matchKey` below. */
  keys?: string;
  group: 'plan' | 'crew' | 'edit' | 'view';
  danger?: boolean;
  enabled: (c: CmdCtx) => boolean;
  run: (c: CmdCtx) => Promise<CmdResult>;
}

// ── the primitives every command is built from ──────────────────────────────────

/**
 * Move a job's window, and hand back the inverse.
 *
 * THE INVERSE USES THE REVISION THE SERVER JUST RETURNED, not the one we sent. Undoing with the old
 * revision would be refused by the optimistic lock every single time — the write we are undoing is
 * precisely what made it stale.
 */
async function moveWindow(i: A.ScheduleItem, start: Ymd, end: Ymd, reason: string): Promise<() => Promise<void>> {
  const before = { start: i.plannedStart!, end: i.plannedEnd! };
  const r = await A.setWorkpackageWindow(i.projectId, i.workpackageId, {
    start, end, reason, expectedRevision: i.planRevision, correlationId: correlationId(),
  });
  return async () => {
    await A.setWorkpackageWindow(i.projectId, i.workpackageId, {
      start: before.start, end: before.end, reason: 'ongedaan gemaakt',
      expectedRevision: r.revision, correlationId: correlationId(),
    });
  };
}

/** Shift every selected job by N days, keeping its length. */
async function shiftBy(c: CmdCtx, days: number, reason: string): Promise<CmdResult> {
  const jobs = workpackages(c.selection).filter((i) => i.plannedStart && i.plannedEnd);
  if (jobs.length === 0) return { message: 'Deze taak heeft nog geen planning; open ze om ze in te plannen.' };
  const undos: Array<() => Promise<void>> = [];
  for (const i of jobs) {
    undos.push(await moveWindow(i, addDays(i.plannedStart!, days), addDays(i.plannedEnd!, days), reason));
  }
  return {
    message: `${jobs.length === 1 ? jobs[0].title : `${jobs.length} taken`} ${reason}.`,
    undo: async () => { for (const u of undos.reverse()) await u(); },
  };
}

/** Grow or shrink from the END of the window — the edge a planner drags. */
async function resizeBy(c: CmdCtx, days: number): Promise<CmdResult> {
  const jobs = workpackages(c.selection).filter((i) => i.plannedStart && i.plannedEnd);
  if (jobs.length === 0) return { message: 'Deze taak heeft nog geen planning.' };
  const undos: Array<() => Promise<void>> = [];
  let refused = 0;
  for (const i of jobs) {
    // A ONE-DAY JOB CANNOT SHRINK. Refusing here rather than sending `end < start` keeps the server's
    // error list about real problems, and lets the message name what actually happened.
    if (days < 0 && daysBetween(i.plannedStart!, i.plannedEnd!) === 0) { refused++; continue; }
    undos.push(await moveWindow(i, i.plannedStart!, addDays(i.plannedEnd!, days),
      days > 0 ? 'verlengd in de kalender' : 'ingekort in de kalender'));
  }
  if (undos.length === 0) return { message: 'Deze taak duurt al één dag; korter kan niet.' };
  return {
    message: `${undos.length} ${undos.length === 1 ? 'taak' : 'taken'} ${days > 0 ? 'verlengd' : 'ingekort'}`
      + (refused > 0 ? ` — ${refused} stond al op één dag.` : '.'),
    undo: async () => { for (const u of undos.reverse()) await u(); },
  };
}

// ── the catalogue ───────────────────────────────────────────────────────────────

const count = (c: CmdCtx, one: string, many: (n: number) => string): string => {
  const n = workpackages(c.selection).length;
  return n <= 1 ? one : many(n);
};

export const COMMANDS: Command[] = [
  // ── planning ──────────────────────────────────────────────────────────────────
  {
    id: 'open', group: 'edit', keys: 'Enter',
    label: () => 'Openen',
    enabled: (c) => c.selection.length > 0,
    // GUARDED, because `enabled` and `run` are evaluated at different moments: a keyboard Enter can
    // arrive in the same tick as the selection change that emptied it.
    run: async (c) => { if (c.selection[0]) openSubject(c, c.selection[0]); return {}; },
  },
  {
    id: 'day-later', group: 'plan', keys: 'mod+ArrowRight',
    label: (c) => count(c, 'Een dag later', (n) => `${n} taken een dag later`),
    enabled: (c) => c.selection.length > 0,
    run: (c) => shiftBy(c, 1, 'een dag opgeschoven'),
  },
  {
    id: 'day-earlier', group: 'plan', keys: 'mod+ArrowLeft',
    label: (c) => count(c, 'Een dag vroeger', (n) => `${n} taken een dag vroeger`),
    enabled: (c) => c.selection.length > 0,
    run: (c) => shiftBy(c, -1, 'een dag vervroegd'),
  },
  {
    id: 'next-week', group: 'plan', keys: 'mod+shift+ArrowRight',
    label: (c) => count(c, 'Naar volgende week', (n) => `${n} taken naar volgende week`),
    enabled: (c) => c.selection.length > 0,
    run: (c) => shiftBy(c, 7, 'naar volgende week geschoven'),
  },
  {
    id: 'prev-week', group: 'plan', keys: 'mod+shift+ArrowLeft',
    label: (c) => count(c, 'Naar vorige week', (n) => `${n} taken naar vorige week`),
    enabled: (c) => c.selection.length > 0,
    run: (c) => shiftBy(c, -7, 'naar vorige week geschoven'),
  },
  {
    id: 'longer', group: 'plan', keys: '+',
    label: (c) => count(c, 'Een dag langer', (n) => `${n} taken een dag langer`),
    enabled: (c) => c.selection.length > 0,
    run: (c) => resizeBy(c, 1),
  },
  {
    id: 'shorter', group: 'plan', keys: '-',
    label: (c) => count(c, 'Een dag korter', (n) => `${n} taken een dag korter`),
    enabled: (c) => c.selection.length > 0,
    run: (c) => resizeBy(c, -1),
  },

  // ── the crew ──────────────────────────────────────────────────────────────────
  {
    id: 'unassign', group: 'crew', keys: 'Delete', danger: true,
    label: (c) => {
      const n = pairs(c.selection).length;
      return n <= 1 ? 'Medewerker weghalen' : `${n} toewijzingen weghalen`;
    },
    enabled: (c) => pairs(c.selection).some((i) => i.assignmentId !== null),
    run: async (c) => {
      // WITHDRAWALS RUN LAST-FIRST WITHIN A JOB. Taking the supervisor off before the person who needs
      // them is refused by the database — correctly — so the order that succeeds is the one that leaves
      // a legal crew at every step. Sorting supervisors to the back does that without knowing the rule.
      const targets = pairs(c.selection).filter((i) => i.assignmentId !== null);
      const supervises = (i: A.ScheduleItem) =>
        !!c.crew.find((m) => m.actorId === i.actorId)?.maySupervise;
      targets.sort((a, b) => Number(supervises(a)) - Number(supervises(b)));

      const undos: Array<() => Promise<void>> = [];
      for (const i of targets) {
        const hours = i.allocatedMinutes / 60;
        await A.unassign(i.projectId, i.assignmentId!, correlationId());
        undos.push(async () => {
          await A.assign(i.projectId, {
            workpackageId: i.workpackageId, resourceId: i.resourceId,
            allocatedHours: hours, reason: 'ongedaan gemaakt', correlationId: correlationId(),
          });
        });
      }
      return {
        message: `${targets.length === 1 ? targets[0].resourceName : `${targets.length} toewijzingen`} weggehaald.`,
        // Re-assigned in REVERSE, so a supervisor goes back on before the person who may not be alone.
        undo: async () => { for (const u of undos.reverse()) await u(); },
      };
    },
  },

  // ── duplicating work ──────────────────────────────────────────────────────────
  {
    id: 'copy', group: 'edit', keys: 'mod+c',
    label: (c) => count(c, 'Kopiëren', (n) => `${n} taken kopiëren`),
    enabled: (c) => c.selection.length > 0,
    run: async (c) => {
      c.setClipboard(pairs(c.selection));
      const n = pairs(c.selection).length;
      return { message: `${n} ${n === 1 ? 'taak' : 'taken'} gekopieerd. Kies een dag en plak met ⌘V.` };
    },
  },
  {
    id: 'duplicate', group: 'edit', keys: 'mod+d',
    label: () => 'Dupliceren…',
    enabled: (c) => c.selection.length === 1 || workpackages(c.selection).length === 1,
    run: async (c) => { openCopy(c, workpackages(c.selection)[0]); return {}; },
  },
  {
    id: 'split', group: 'edit',
    label: () => 'Opsplitsen…',
    enabled: (c) => workpackages(c.selection).length === 1,
    run: async (c) => { openSplit(c, workpackages(c.selection)[0]); return {}; },
  },
];

// ── the two that open the panel ─────────────────────────────────────────────────
// COPY AND SPLIT NEED A PHASE, and the schedule does not carry one. Guessing puts work in the wrong
// phase of the wrong werf, which is worse than one confirmation — and `uq_wp_live_per_phase_discipline`
// means a copy usually needs a different phase or discipline anyway. That is a decision, not a default.

function openSubject(c: CmdCtx, i: A.ScheduleItem) {
  const crew = new Map<number, { resourceId: number; hours: number; assignmentId: number | null }>();
  for (const r of c.all.filter((x) => x.workpackageId === i.workpackageId)) {
    crew.set(r.resourceId, { resourceId: r.resourceId, hours: r.allocatedMinutes / 60, assignmentId: r.assignmentId });
  }
  c.openPanel({
    projectId: i.projectId, projectCode: i.projectCode, projectName: i.projectName,
    officialNumber: i.officialNumber, customerName: i.customerName, workpackageId: i.workpackageId,
    title: i.title, scope: i.scope, disciplineCode: i.disciplineCode,
    lifecycleState: i.lifecycleState, plannedStart: i.plannedStart, plannedEnd: i.plannedEnd,
    planRevision: i.planRevision, assignees: [...crew.values()],
  });
}

function openCopy(c: CmdCtx, i: A.ScheduleItem) {
  const crew = c.all.filter((x) => x.workpackageId === i.workpackageId);
  const seen = new Map<number, { resourceId: number; hours: number; assignmentId: null }>();
  for (const r of crew) seen.set(r.resourceId, { resourceId: r.resourceId, hours: r.allocatedMinutes / 60, assignmentId: null });
  c.openPanel({
    projectId: i.projectId, projectCode: i.projectCode, projectName: i.projectName,
    officialNumber: i.officialNumber, customerName: i.customerName, workpackageId: null,
    title: `${i.title} (kopie)`, scope: i.scope, disciplineCode: i.disciplineCode,
    lifecycleState: 'draft', plannedStart: i.plannedStart, plannedEnd: i.plannedEnd,
    planRevision: 0, assignees: [...seen.values()],
  });
}

function openSplit(c: CmdCtx, i: A.ScheduleItem) {
  // THE SECOND HALF ONLY. The original is left exactly as it is until the planner saves, because a
  // split that halves the original and is then cancelled has destroyed a day of planning for nothing.
  // The old version halved first and opened the panel second; cancelling left the job silently short.
  const crew = c.all.filter((x) => x.workpackageId === i.workpackageId);
  const half = new Map<number, { resourceId: number; hours: number; assignmentId: null }>();
  for (const r of crew) half.set(r.resourceId, { resourceId: r.resourceId, hours: r.allocatedMinutes / 120, assignmentId: null });
  const from = i.plannedEnd ? addDays(i.plannedEnd, 1) : i.plannedStart;
  c.openPanel({
    projectId: i.projectId, projectCode: i.projectCode, projectName: i.projectName,
    officialNumber: i.officialNumber, customerName: i.customerName, workpackageId: null,
    title: `${i.title} (deel 2)`, scope: i.scope, disciplineCode: i.disciplineCode,
    lifecycleState: 'draft', plannedStart: from, plannedEnd: from,
    planRevision: 0, assignees: [...half.values()],
  });
}

/**
 * Paste the clipboard onto a day.
 *
 * Not in the catalogue because it needs a DESTINATION the catalogue cannot know — the day under the
 * cursor, or the day the calendar is on. The caller supplies it.
 */
export async function pasteOnto(c: CmdCtx, day: Ymd): Promise<CmdResult> {
  if (c.clipboard.length === 0) return { message: 'Niets om te plakken.' };
  // One panel per copied job would be a stack of dialogs, so paste is deliberately limited to the
  // single-job case and says so instead of half-doing the rest.
  const jobs = workpackages(c.clipboard);
  if (jobs.length > 1) {
    return { message: `${jobs.length} taken op het klembord — plakken werkt voorlopig met één taak tegelijk.` };
  }
  const i = jobs[0];
  const span = i.plannedStart && i.plannedEnd ? daysBetween(i.plannedStart, i.plannedEnd) : 0;
  const crew = new Map<number, { resourceId: number; hours: number; assignmentId: null }>();
  for (const r of c.clipboard.filter((x) => x.workpackageId === i.workpackageId)) {
    crew.set(r.resourceId, { resourceId: r.resourceId, hours: r.allocatedMinutes / 60, assignmentId: null });
  }
  c.openPanel({
    projectId: i.projectId, projectCode: i.projectCode, projectName: i.projectName,
    officialNumber: i.officialNumber, customerName: i.customerName, workpackageId: null,
    title: `${i.title} (kopie)`, scope: i.scope, disciplineCode: i.disciplineCode,
    lifecycleState: 'draft', plannedStart: day, plannedEnd: addDays(day, span),
    planRevision: 0, assignees: [...crew.values()],
  });
  return {};
}

// ── keyboard matching ───────────────────────────────────────────────────────────

/**
 * Does this event match a command's key spec?
 *
 * `mod` is Cmd on a Mac and Ctrl elsewhere — hard-coding one of them makes the app feel foreign on
 * whichever platform lost the toss. Comparison is case-insensitive on the key name so `mod+D` and
 * `mod+d` are the same shortcut whether or not Shift was involved in producing the character.
 */
export function matchKey(e: KeyboardEvent, spec: string): boolean {
  const parts = spec.split('+');
  const key = parts[parts.length - 1];
  const want = new Set(parts.slice(0, -1));
  const mod = e.metaKey || e.ctrlKey;
  if (want.has('mod') !== mod) return false;
  if (want.has('shift') !== e.shiftKey) return false;
  if (want.has('alt') !== e.altKey) return false;
  return e.key.toLowerCase() === key.toLowerCase();
}

/** "⌘D" / "Ctrl+D" — what the menu prints beside a label. */
export function keyHint(spec: string | undefined, mac: boolean): string {
  if (!spec) return '';
  return spec
    .replace('mod+', mac ? '⌘' : 'Ctrl+')
    .replace('shift+', mac ? '⇧' : 'Shift+')
    .replace('alt+', mac ? '⌥' : 'Alt+')
    .replace('ArrowRight', '→').replace('ArrowLeft', '←')
    .replace('ArrowUp', '↑').replace('ArrowDown', '↓')
    .replace('Delete', mac ? '⌫' : 'Del')
    .replace('Enter', '↵');
}

export const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
